import axios, { AxiosResponse } from 'axios';
import { useState } from 'react';
import { createMultiPartRequest, createRequest } from '../helpers/api_utils';
import { apiUrl } from './api_url';
import { assessmentFlowApiEndpoint  } from './assessment_api';

const apiEndpoint = apiUrl + '/behaviour-interview';

export async function interviewStart(body: {attribute: string}) {

  return fetch(createRequest(apiEndpoint + '/start', 'POST', 'application/json', true, body));
}

export async function interviewAnswer(assessmentId: string) {

  return fetch(createRequest(`${apiEndpoint}/start?assessment_id=${assessmentId}`, 'POST', 'application/json', true));
}

export async function interviewAudioAnswer(instanceId: string, questionId: string, answer?: string, audioBlob?: Blob) {
  let formData = new FormData();
  formData.append('user_question_assessment_id', instanceId);
  formData.append('question_id', questionId);

  if (answer) {
    formData.append('answer', answer);
  }

  if (audioBlob) {
    const audioFile = new File([audioBlob], 'answer.webm', { type: 'audio/webm' });
    formData.append('file', audioFile);
  }

  // formData.append('answer', answer);
  // const audioFile = new File([audioBlob], 'answer.webm', { type: 'audio/webm' });
  // formData.append('file', audioFile);

  return fetch(createMultiPartRequest(apiEndpoint + '/answer/audio', 'POST', true, formData));
}

export const useUploadForm = () => {
  const [uploadStatus, setUploadStatus] = useState<'initial' | 'loading' | 'success' | 'error'>('initial');
  const [progress, setProgress] = useState(0);

  const awsUploadWithProgress = async (
    uploadLink: string,
    videoBlob: Blob,
    onProgress: (percent: number) => void,
  ): Promise<Response> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = (e.loaded / e.total) * 100;
          onProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(new Response(xhr.response, {
            status: xhr.status,
            statusText: xhr.statusText,
          }));
        } else {
          reject(new Error(`AWS upload failed: ${xhr.statusText}`));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Network error')));
      xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

      xhr.open('PUT', uploadLink);
      xhr.setRequestHeader('Content-Type', videoBlob.type);
      xhr.send(videoBlob);
    });
  };

  const uploadForm = async (
    instanceId: string,
    questionId: string,
    answer?: string,
    audioBlob?: File,
    awsUploadLink?: string,
    videoBlob?: File,
  ) => {
    const url = apiEndpoint + '/answer/audio';

    setUploadStatus('loading');
    setProgress(0);

    try {
      // Step 1: AWS Upload (if provided) - 0-50%
      if (awsUploadLink && videoBlob) {
        const uploadResponse = await awsUploadWithProgress(
          awsUploadLink,
          videoBlob,
          (percent) => {
            // Map AWS upload progress to 0-50%
            setProgress(Math.min(percent * 0.5, 50));
          },
        );

        if (!uploadResponse.ok) {
          throw new Error('AWS upload failed: ' + uploadResponse.statusText);
        }

        setProgress(50); // Ensure we're at 50% before next step
      }

      // Step 2: Form Upload - 50-100%
      let formData = new FormData();
      formData.append('user_question_assessment_id', instanceId);
      formData.append('question_id', questionId);
      if (answer) {
        formData.append('answer', answer);
      }

      if (audioBlob) {
        // const audioFile = new File([audioBlob], 'answer.webm', { type: 'audio/webm' });
        formData.append('file', audioBlob);
      }

      const response: AxiosResponse = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;

          const uploadPercent = (progressEvent.loaded / progressEvent.total) * 100;
          // Map form upload progress to 50-100% (or 0-100% if no AWS upload)
          const startProgress = awsUploadLink && videoBlob ? 50 : 0;
          const progressRange = awsUploadLink && videoBlob ? 50 : 100;
          const totalPercent = Math.min(
            startProgress + (uploadPercent * progressRange) / 100,
            startProgress + progressRange - 8,
          );

          setProgress(totalPercent);
        },
      });

      setProgress(100);
      setUploadStatus('success');
      return response.data;
    } catch (error) {
      setUploadStatus('error');
      console.error('Upload error:', error);
      throw error;
    } finally {
      setTimeout(() => {
        setUploadStatus('initial');
        setProgress(0);
      }, 300);
    }
  };

  const uploadQuestionAnswerSkill = async (
    userAssessmentQnaId: string,
    timeExpired: string,
    textAnswer?: string,
    videoFile?: File,
  ) => {
    const url = assessmentFlowApiEndpoint + '/upload';
    let formData = new FormData();
    formData.append('user_assessment_qna_id', userAssessmentQnaId);
    formData.append('time_expired_str', timeExpired.toString());

    if (textAnswer && videoFile) {
      formData.append('user_answer', textAnswer);
      formData.append('file', videoFile);
    }

    setUploadStatus('loading');

    try {
      const response: AxiosResponse = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          const percent = Math.min(
            (progressEvent.loaded / progressEvent.total) * 100,
            92,
          );
          setProgress(percent);
        },
      });

      setProgress(100);
      setUploadStatus('success');
      return response.data;
    } catch (error) {
      setUploadStatus('error');
      throw error;
    } finally {
      setTimeout(() => {
        setUploadStatus('initial');
        setProgress(0);
      }, 300);
    }
  };

  const uploadQuestionAnswerSkillWithNavigation = async (
    userAssessmentQnaId: string,
    timeExpired: string,
    userAssessmentId: string,
    textAnswer?: string,
    videoFile?: File,
  ) => {
    const url = assessmentFlowApiEndpoint + '/upload';
    let formData = new FormData();
    formData.append('user_assessment_qna_id', userAssessmentQnaId);
    formData.append('time_expired_str', timeExpired.toString());

    if (textAnswer && videoFile) {
      formData.append('user_answer', textAnswer);
      formData.append('file', videoFile);
    }

    setUploadStatus('loading');

    try {
      // Step 1: Upload - 0-80%
      const uploadResponse: AxiosResponse = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          // Map upload to 0-80% of total progress
          const percent = Math.min(
            (progressEvent.loaded / progressEvent.total) * 80,
            80,
          );
          setProgress(percent);
        },
      });

      // Step 2: Navigate to next question - 80-100%
      setProgress(85);

      const navResponse = await fetch(
        createRequest(
          assessmentFlowApiEndpoint + `/next?user_assessment_id=${userAssessmentId}`,
          'GET',
          'application/json',
          true,
        ),
      );

      setProgress(95);
      const navData = await navResponse.json();
      setProgress(100);

      setUploadStatus('success');

      return {
        uploadData: uploadResponse.data,
        navigationData: navData,
      };
    } catch (error) {
      setUploadStatus('error');
      throw error;
    } finally {
      setTimeout(() => {
        setUploadStatus('initial');
        setProgress(0);
      }, 300);
    }
  };

  return { uploadQuestionAnswerSkill, uploadForm, uploadQuestionAnswerSkillWithNavigation, uploadStatus, setUploadStatus, progress };
};

export async function assessmentBEIQuestion(
  body: {
      assessmentId: string;
      questions: {
        question: string;
        attribute: string;
      }[];
      timeLimit: number;
    },
) {

  return fetch(createRequest(apiEndpoint + '/questions', 'POST', 'application/json', true, body));
}

export async function getAssessmentBEIQuestion(assessmentId: string) {
  return fetch(createRequest(`${apiEndpoint}/questions?assessment_id=${assessmentId}`, 'GET', 'application/json', true));
}

export async function getBEIResults(assessmentId: string, userId: string) {
  return fetch(
    createRequest(
      `${apiEndpoint}/result?assessment_id=${assessmentId}&user_id=${userId}`,
      'GET',
      'application/json',
      true,
    ),
  );
}
